import { Fragment, ReactNode, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  IconButton,
  Stack,
  styled,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import Edit from 'assets/icons/Edit.png'
import EquipmentAddressDialog from 'components/equipment-address-dialog/equipment-address-dialog.component'
import { TripPayload } from 'store/trips/trips.types'
import { removeEnteredTrailerTask, removeTaskById } from 'store/trips/trips.slice'
import { useUpdateTripTaskStatusMutation } from 'store/trips/trips.api'
import { useSessionOrderEventCleanup } from 'store/trips/use-session-order-event-cleanup'

import TripDetailsTableCell from '../../trip-details-table-cell/trip-details-table-cell'
import TripDetailsTableAction from '../trip-details-table-action/trip-details-table-action'
import TripDetailsTask from '../../trip-details-task/trip-details-task'
import TripBuilderRowAction from '../../../trip-builder-selection/trip-builder-row-action'
import {
  formatTripEventDateTime,
  formatTripTaskStatus,
  getMarkAsCompletedLabel,
  isEnteredDropTrailerTask,
  isEnteredHookTrailerTask,
  isEquipmentOnlyTask,
  isOrderLinkedDropTrailerTask,
  isOrderLinkedHookTrailerTask,
  sharesOrderWithNextTask,
  shouldSeparateOrderTasks,
  showsOrderEventDetails,
  showsOrderId,
  showsTripTaskStatus,
} from './trip-details-table-body.utils'

interface TripDetailsTableBodyProps {
  tripTasks: TripPayload[]
  isLandingPage?: boolean
  isDriverBuilder?: boolean
  onTaskStatusUpdated?: () => void
  onOpenDropAddress?: () => void
  onChangeTractor?: () => void
  onChangeTrailer?: (trailerId: string | null) => void
  onChangePickup?: (trailerId: string | null) => void
  beforeTripSlot?: ReactNode
  afterHookTractorSlot?: ReactNode
  afterHookTrailerSlot?: (trailerId: string | null) => ReactNode
  beforeDeliverySlot?: ReactNode
  beforeDeliveryOrderEventId?: number
  beforeDropTrailerSlot?: (trailerId: string | null) => ReactNode
  afterDropTrailerSlot?: ReactNode
}

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {},
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}))

const SlotRow = ({
  children,
  colSpan,
}: {
  children: ReactNode
  colSpan: number
}) => {
  if (!children) return null
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: 'none' }}>
        {children}
      </TableCell>
    </TableRow>
  )
}

const TripDetailsTableBody = ({
  tripTasks,
  isLandingPage = false,
  isDriverBuilder = false,
  onTaskStatusUpdated,
  onOpenDropAddress,
  onChangeTractor,
  onChangeTrailer,
  onChangePickup,
  beforeTripSlot,
  afterHookTractorSlot,
  afterHookTrailerSlot,
  beforeDeliverySlot,
  beforeDeliveryOrderEventId,
  beforeDropTrailerSlot,
  afterDropTrailerSlot,
}: TripDetailsTableBodyProps) => {
  const dispatch = useDispatch()
  const { deleteSessionOrderEvent } = useSessionOrderEventCleanup()
  const [editAddressOpen, setEditAddressOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<TripPayload | null>(null)
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null)
  const [updateTripTaskStatus] = useUpdateTripTaskStatusMutation()

  const colSpan = isLandingPage ? 5 : isDriverBuilder ? 6 : 5

  const handleMarkAsCompleted = useCallback(
    async (tripTask: TripPayload) => {
      if (!tripTask.tripId || !tripTask.tripTaskId) {
        console.error('Missing tripId or tripTaskId')
        return
      }

      try {
        setUpdatingTaskId(tripTask.tripTaskId)
        await updateTripTaskStatus({
          tripId: tripTask.tripId,
          taskId: tripTask.tripTaskId,
          status: 'COMPLETED',
        }).unwrap()
        onTaskStatusUpdated?.()
      } catch (error) {
        console.error('Failed to update trip task status:', error)
      } finally {
        setUpdatingTaskId(null)
      }
    },
    [onTaskStatusUpdated, updateTripTaskStatus]
  )

  const getBgColor = (tripTask: TripPayload) => {
    if (
      isOrderLinkedHookTrailerTask(tripTask) ||
      isEnteredHookTrailerTask(tripTask) ||
      tripTask.type === 'PICKUP'
    ) {
      return 'primary.light'
    }

    if (
      tripTask.type === 'DELIVERY' ||
      isOrderLinkedDropTrailerTask(tripTask) ||
      isEnteredDropTrailerTask(tripTask)
    ) {
      return 'success.light'
    }

    return 'secondary.light'
  }

  const handleEditEquipmentAddress = useCallback((tripTask: TripPayload) => {
    setEditingTask(tripTask)
    setEditAddressOpen(true)
  }, [])

  const handleCloseEditAddress = useCallback(() => {
    setEditAddressOpen(false)
    setEditingTask(null)
  }, [])

  const handleRemoveOrderTask = async (tripTask: TripPayload) => {
    if (
      isEnteredDropTrailerTask(tripTask) ||
      isEnteredHookTrailerTask(tripTask)
    ) {
      dispatch(
        removeEnteredTrailerTask({
          type: tripTask.type,
          sourceOrderPkId: tripTask.sourceOrderPkId,
          orderId: tripTask.orderId,
        })
      )
      return
    }

    if (tripTask.orderEventId == null) return

    await deleteSessionOrderEvent(tripTask.orderEventId)
    dispatch(
      removeTaskById({
        orderEventId: tripTask.orderEventId,
        type: tripTask.type,
      })
    )
  }

  const isSameOrder = (a: TripPayload, b: TripPayload) => {
    if (a.sourceOrderPkId != null && b.sourceOrderPkId != null) {
      return a.sourceOrderPkId === b.sourceOrderPkId
    }
    return a.orderId != null && a.orderId === b.orderId
  }

  const isFirstPickupLikeForOrder = (tripTask: TripPayload, index: number) => {
    const isPickupLike = (task: TripPayload) =>
      task.type === 'PICKUP' ||
      isOrderLinkedHookTrailerTask(task) ||
      isEnteredHookTrailerTask(task)

    if (!isPickupLike(tripTask)) return false

    const firstPickupLikeIndex = tripTasks.findIndex(
      (task) => isPickupLike(task) && isSameOrder(task, tripTask)
    )

    return firstPickupLikeIndex === index
  }

  // Delivery-only orders have no PICKUP / order-linked hook to attach Change Pickup to.
  // Entered hook trailers show Remove, so Change lives on the delivery row instead.
  const isFirstDeliveryOnlyForOrder = (
    tripTask: TripPayload,
    index: number
  ) => {
    if (tripTask.type !== 'DELIVERY') return false

    const orderHasChangePickupRow = tripTasks.some(
      (task) =>
        isSameOrder(task, tripTask) &&
        (task.type === 'PICKUP' || isOrderLinkedHookTrailerTask(task))
    )
    if (orderHasChangePickupRow) return false

    const firstDeliveryIndex = tripTasks.findIndex(
      (task) => task.type === 'DELIVERY' && isSameOrder(task, tripTask)
    )

    return firstDeliveryIndex === index
  }

  const getRowAction = (tripTask: TripPayload, index: number) => {
    if (!isDriverBuilder || isLandingPage) return null

    if (tripTask.type === 'HOOK_TRACTOR') {
      return (
        <TripBuilderRowAction
          label="Change Tractor"
          onClick={() => onChangeTractor?.()}
        />
      )
    }

    if (
      tripTask.type === 'HOOK_TRAILER' &&
      tripTask.orderEventId == null &&
      tripTask.sourceOrderPkId == null &&
      !tripTask.orderId
    ) {
      return (
        <TripBuilderRowAction
          label="Change Trailer"
          onClick={() => onChangeTrailer?.(tripTask.trailerId)}
        />
      )
    }

    // When entered hook replaces equipment hook, keep Change Trailer on equipment drop.
    if (
      tripTask.type === 'DROP_TRAILER' &&
      tripTask.orderEventId == null &&
      tripTask.sourceOrderPkId == null &&
      !tripTask.orderId &&
      !tripTasks.some(
        (task) =>
          task.type === 'HOOK_TRAILER' &&
          task.trailerId === tripTask.trailerId &&
          task.orderEventId == null &&
          task.sourceOrderPkId == null &&
          !task.orderId
      )
    ) {
      return (
        <TripBuilderRowAction
          label="Change Trailer"
          onClick={() => onChangeTrailer?.(tripTask.trailerId)}
        />
      )
    }

    if (isEnteredHookTrailerTask(tripTask)) {
      return (
        <TripBuilderRowAction
          label="Remove"
          color="error"
          onClick={() => handleRemoveOrderTask(tripTask)}
        />
      )
    }

    if (isFirstPickupLikeForOrder(tripTask, index)) {
      return (
        <TripBuilderRowAction
          label="Change Pickup"
          onClick={() => onChangePickup?.(tripTask.trailerId)}
        />
      )
    }

    if (isFirstDeliveryOnlyForOrder(tripTask, index)) {
      return (
        <TripBuilderRowAction
          label="Change"
          onClick={() => onChangePickup?.(tripTask.trailerId)}
        />
      )
    }

    // Enter-address drop only — order-sourced drops are managed via Change Pickup
    if (isEnteredDropTrailerTask(tripTask)) {
      return (
        <TripBuilderRowAction
          label="Remove"
          color="error"
          onClick={() => handleRemoveOrderTask(tripTask)}
        />
      )
    }

    return null
  }

  const shouldShowBeforeDropTrailer = (index: number) => {
    if (!beforeDropTrailerSlot) return false
    const task = tripTasks[index]
    const next = tripTasks[index + 1]
    const nextIsEquipmentDrop =
      next?.type === 'DROP_TRACTOR' ||
      (next?.type === 'DROP_TRAILER' &&
        next.orderEventId == null &&
        next.sourceOrderPkId == null &&
        !next.orderId)
    const isLastTransport =
      (task.type === 'PICKUP' ||
        task.type === 'DELIVERY' ||
        isOrderLinkedHookTrailerTask(task) ||
        isOrderLinkedDropTrailerTask(task) ||
        isEnteredDropTrailerTask(task) ||
        isEnteredHookTrailerTask(task)) &&
      (!next || nextIsEquipmentDrop)
    return isLastTransport
  }

  return (
    <>
      <TableBody>
        <SlotRow colSpan={colSpan}>{beforeTripSlot}</SlotRow>
        {tripTasks.map((tripTask: TripPayload, index: number) => {
          const eventDateTime = formatTripEventDateTime(tripTask.startTime)
          const statusValues = formatTripTaskStatus(tripTask)
          const rowAction = getRowAction(tripTask, index)

          return (
            <Fragment key={`${tripTask.sequenceId}-${index}`}>
              {shouldSeparateOrderTasks(tripTasks, index) && (
                <tr style={{ paddingTop: 4, display: 'block' }} />
              )}
              {tripTask.type === 'DELIVERY' &&
                tripTask.orderEventId === beforeDeliveryOrderEventId && (
                  <SlotRow colSpan={colSpan}>{beforeDeliverySlot}</SlotRow>
                )}
              <StyledTableRow
                sx={{
                  bgcolor: getBgColor(tripTask),
                  '& td': {
                    borderBottom: sharesOrderWithNextTask(tripTasks, index)
                      ? 'none'
                      : undefined,
                  },
                }}
              >
                {!isLandingPage && (
                  <TripDetailsTableAction
                    tripTasks={tripTasks}
                    index={index}
                    hideLegacyIcon={isDriverBuilder}
                    onOpenDropAddress={onOpenDropAddress}
                  />
                )}
                <TripDetailsTask tripTask={tripTask} />
                <TripDetailsTableCell
                  value1={showsOrderId(tripTask) ? tripTask.orderId : ''}
                  value2={showsOrderId(tripTask) ? 'Order' : ''}
                  minWidth={120}
                />
                <TripDetailsTableCell
                  value1={
                    showsOrderEventDetails(tripTask) ? eventDateTime.date : ''
                  }
                  value2={
                    showsOrderEventDetails(tripTask) ? eventDateTime.time : ''
                  }
                  minWidth={150}
                />
                {isEquipmentOnlyTask(tripTask) ? (
                  <TripDetailsTableCell minWidth={260}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      {!isLandingPage && (
                        <IconButton
                          size="small"
                          aria-label="edit address"
                          onClick={() => handleEditEquipmentAddress(tripTask)}
                          sx={{ p: 0.5, mt: -0.5 }}
                        >
                          <img src={Edit} alt="edit" width={20} height={20} />
                        </IconButton>
                      )}
                      <Box sx={{ flex: 1 }}>{tripTask.address}</Box>
                    </Box>
                  </TripDetailsTableCell>
                ) : (
                  <TripDetailsTableCell
                    value1={
                      showsOrderEventDetails(tripTask) ? tripTask.name : ''
                    }
                    value2={
                      showsOrderEventDetails(tripTask) ||
                      tripTask.type?.includes('DROP')
                        ? tripTask.address
                        : ''
                    }
                    minWidth={260}
                  />
                )}
                {isLandingPage && (() => {
                  const canShowStatus = showsTripTaskStatus(tripTask)
                  const isCompleted = tripTask.taskStatus === 'COMPLETED'
                  const showMarkComplete =
                    canShowStatus &&
                    !isCompleted &&
                    !!tripTask.tripId &&
                    !!tripTask.tripTaskId

                  if (showMarkComplete) {
                    return (
                      <TripDetailsTableCell minWidth={150}>
                        <Button
                          size="small"
                          disabled={updatingTaskId === tripTask.tripTaskId}
                          onClick={() => handleMarkAsCompleted(tripTask)}
                          sx={{
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            paddingTop: '6px',
                            paddingBottom: '6px',
                            borderRadius: '6px',
                            textTransform: 'capitalize',
                            fontWeight: 700,
                            minWidth: 'auto',
                            backgroundColor: 'primary.main',
                            color: 'common.white',
                            border: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                              boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'primary.main',
                              color: 'common.white',
                              opacity: 0.7,
                            },
                          }}
                        >
                          {getMarkAsCompletedLabel(tripTask)}
                        </Button>
                      </TripDetailsTableCell>
                    )
                  }

                  return (
                    <TripDetailsTableCell
                      value1={statusValues.value1}
                      value2={statusValues.value2}
                      minWidth={150}
                    />
                  )
                })()}
                {isDriverBuilder && !isLandingPage && (
                  <TableCell sx={{ minWidth: 140, verticalAlign: 'middle' }}>
                    <Stack alignItems="flex-end">{rowAction}</Stack>
                  </TableCell>
                )}
              </StyledTableRow>

              {tripTask.type === 'HOOK_TRACTOR' && (
                <SlotRow colSpan={colSpan}>{afterHookTractorSlot}</SlotRow>
              )}
              {tripTask.type === 'HOOK_TRAILER' &&
                tripTask.orderEventId == null &&
                tripTask.sourceOrderPkId == null &&
                !tripTask.orderId && (
                  <SlotRow colSpan={colSpan}>
                    {afterHookTrailerSlot?.(tripTask.trailerId)}
                  </SlotRow>
                )}
              {shouldShowBeforeDropTrailer(index) && (
                  <SlotRow colSpan={colSpan}>
                    {beforeDropTrailerSlot?.(tripTask.trailerId)}
                  </SlotRow>
                )}
              {tripTask.type === 'DROP_TRAILER' &&
                (tripTasks[index + 1]?.type === 'DROP_TRACTOR' ||
                  tripTasks[index + 1] == null) && (
                  <SlotRow colSpan={colSpan}>{afterDropTrailerSlot}</SlotRow>
                )}
            </Fragment>
          )
        })}
      </TableBody>

      <EquipmentAddressDialog
        open={editAddressOpen}
        handleClose={handleCloseEditAddress}
        sequenceId={editingTask?.sequenceId ?? null}
        initialAddress={editingTask?.address ?? ''}
      />
    </>
  )
}

export default TripDetailsTableBody
